import React from 'react';
import { useNavigate } from 'react-router';
import type { IndustryType } from '@gcv/shared';
import { DueDiligenceStatus, NonCannabisIndustryTypes } from '@gcv/shared';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Box } from '@mui/system';

import { BanksApi } from 'api';
import { ReactComponent as NonCannabisIcon } from 'assets/images/store-location-generic-icon.svg';
import { ReactComponent as DispensaryIcon } from 'assets/images/store-location-icon.svg';
import { useComponent } from 'hooks/useComponent';
import { getAppViewStateStore } from 'stores/AppViewStateStore';
import { getFiBankStore } from 'stores/FiBankStore';
import { getFiDispensaryStore } from 'stores/FiDispensaryStore';
import { getFiRelationshipManagerStore } from 'stores/FiRelationshipManagerStore';
import { getFiTemplateStore } from 'stores/FiTemplateStore';
import { getSnackbarStore } from 'stores/SnackBarStore';
import type { Column, Row } from 'ui';
import {
  Button,
  Container,
  ContainerItem,
  Spinner,
  Table,
  TableContainer,
  Text,
  Tooltip,
  ZeroState
} from 'ui';
import { DateTimeHelpers } from 'util/dateTime.util';
import { getLicenseType } from 'util/org.util';
import useQueryString from 'util/queryString.util';
import { capitalizeWords } from 'util/strings.util';
import { ChangeStatusModal } from '../components/change-status-modal';
import { UnarchiveInvitedAccountModal } from '../components/unarchive-invited-account-modal';
import type { InvitedDispensary } from '../model';
import { model } from '../model';

interface ReviewCrbRow {
  id: string;
  name: string;
  parent_muo_name?: string;
  city: string;
  state: string;
  license_type: string;
  created_on: string;
  documents: number;
  last_login: string;
  relationship_manager: string;
  invited: boolean;
  invited_id: string;
  invited_by_org_id: string;
  invited_by_email: string;
  due_diligence: string;
  industryType?: IndustryType;
}

export const Invited: React.FC = useComponent(() => {
  const navigate = useNavigate();

  const [unarchiveQuery] = useQueryString('unarchive', '');
  const [orgNameQuery] = useQueryString('orgName', '');

  const banksApi = new BanksApi();

  const appViewStore = getAppViewStateStore();
  const dispStore = getFiDispensaryStore();
  const relationshipManagerStore = getFiRelationshipManagerStore();
  const bank = getFiBankStore().bank;

  const [unarchiveModalOpen, setUnarchiveModalOpen] = React.useState(false);
  const [isSendingInvite, setIsSendingInvite] = React.useState<Record<string, boolean>>({});
  const [selectedAccounts, setSelectedAccounts] = React.useState<string[]>([]);
  const [changeStatusModalOpen, setChangeStatusModalOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (unarchiveQuery) {
      setUnarchiveModalOpen(true);
    }
  }, []);

  const columns: Column<ReviewCrbRow>[] = [
    {
      label: 'Name',
      id: 'name',
      widthPercent: 20,
      format: (row) => (
        <span style={{ display: 'flex' }}>
          {/* need to change icon here when we have information about it being an muo */}
          {/* <MuoIcon style={{ marginRight: '9px', width: '20px', height: '20px'  }}></MuoIcon> */}
          {row.industryType && NonCannabisIndustryTypes.includes(row.industryType) ? (
            <NonCannabisIcon style={{ marginRight: '9px', width: '20px', height: '20px' }} />
          ) : (
            <DispensaryIcon style={{ marginRight: '9px', width: '20px', height: '20px' }}></DispensaryIcon>
          )}
          {row.parent_muo_name ? (
            <Box flex="1">
              <Container padding="0" column justify="flex-start">
                <Text type="body3" content={row.name} />
                <Text
                  type="body3"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                  content={row.parent_muo_name}
                />
              </Container>
            </Box>
          ) : (
            <Box flex="1">
              <Text type="body3" content={row.name} />
            </Box>
          )}
        </span>
      ),
      sortFunction: (a, b) => {
        return a.data.name.toLowerCase() > b.data.name.toLowerCase() ? 1 : -1;
      }
    },
    {
      label: 'Relationship Manager',
      id: 'relationship_manager',
      widthPercent: 15
    },
    {
      label: 'City',
      id: 'city',
      widthPercent: 10,
      format: (row) => capitalizeWords(row.city)
    },
    {
      label: 'State',
      id: 'state',
      widthPercent: 6
    },
    {
      label: 'License Type',
      id: 'license_type',
      widthPercent: 10
    },
    {
      label: 'Due Diligence',
      id: 'due_diligence',
      widthPercent: 15,
      format: (row) => capitalizeWords(row.due_diligence)
    },
    {
      label: 'Created On',
      id: 'created_on',
      widthPercent: 11,
      format: (row) => {
        return DateTimeHelpers.formatISOToTableDateString(row.created_on, bank.iana_timezone);
      }
    },
    {
      label: 'Documents',
      id: 'documents',
      widthPercent: 10
    },
    {
      label: 'Last Login',
      id: 'last_login',
      widthPercent: 15,
      disableRowTooltip: true,
      format: (row) => {
        return row.invited ? (
          <div style={{ width: '129px' }}>
            <Tooltip title={`Resend invite email to ${row.invited_by_email}`}>
              <Button
                fullWidth
                size="small"
                label={'Resend Invite'}
                onClick={() => {
                  const invitedId = row.invited_id;
                  setIsSendingInvite({ ...isSendingInvite, [invitedId]: true });
                  banksApi
                    .resendInviteEmail(row.invited_by_org_id, row.invited_id)
                    .then(() => {
                      getSnackbarStore().showSuccessSnackbarMessage('Invite email has been resent');
                    })
                    .catch((e) => {
                      getSnackbarStore().showErrorSnackbarMessage('Failed to resend invite email');
                    })
                    .finally(() => {
                      setIsSendingInvite({ ...isSendingInvite, [invitedId]: false });
                    });
                }}
                color="primary-outlined"
                isLoading={isSendingInvite[row.invited_id]}
              />
            </Tooltip>
          </div>
        ) : (
          DateTimeHelpers.formatISODateStringToTableDateAndTimeString(row.last_login, bank.iana_timezone)
        );
      }
    }
  ];

  const handleOnView = (id: string) => {
    navigate(`/secure/fi/accounts/${id}/review`);
  };

  const tableRows: Row<ReviewCrbRow>[] = model
    .filterInvitedByAccountsSearchTerm(model.invitedDispensaries)
    .map((dispensary: InvitedDispensary) => {
      const licenseType: string = getLicenseType(dispensary);
      const invitation = getFiDispensaryStore().invitedDispensaries.find((d) => d.id === dispensary.id);
      const onboardingTemplates = getFiTemplateStore().templates.onboarding;
      const templateExists = Object.keys(onboardingTemplates).includes(
        dispensary.assigned_onboarding_template.template_id
      );

      return {
        id: dispensary.id!,
        data: {
          id: dispensary.id!,
          name: dispensary.name || '--',
          city: dispensary.city || '--',
          state: dispensary.state || '--',
          license_type: licenseType || '--',
          created_on: dispensary.date_created || '',
          documents: dispensary.assigned_onboarding_template.number_of_documents || 0,
          last_login: dispensary.last_login_date || '',
          parent_muo_name: dispStore.bankMuos.find((muo) => muo.id === dispensary.muo_id)?.name,
          relationship_manager: relationshipManagerStore.relationshipManagers[dispensary.id] || '--',
          invited: dispensary.invited ? dispensary.invited : false,
          invited_by_org_id: invitation?.invited_by_org_id || '',
          invited_id: invitation?.id || '',
          invited_by_email: invitation?.email || '',
          due_diligence: templateExists
            ? onboardingTemplates[dispensary.assigned_onboarding_template.template_id].name
            : '--',
          industryType: dispensary.industryType
        },
        disabled: dispensary.invited,
        disabledTooltip:
          "An account for this user already exists in Green Check. On their next login, we'll automatically ask them to use the existing account or create a new one.",
        disableSelect: dispensary.invited
      };
    });

  const showFilterZeroState = React.useMemo(() => {
    return tableRows.length === 0 && (model.accountsSearchTerm.length > 0 || model.searchTerms.length > 0);
  }, [tableRows, model.accountsSearchTerm.length, model.searchTerms.length]);

  if (model.isSorting) {
    return <Spinner />;
  }

  return (
    <>
      <Box>
        {showFilterZeroState || model.invitedDispensaries.length ? (
          <TableContainer>
            <Table
              columns={columns}
              rows={tableRows}
              dataCy="invited-table"
              defaultSort={
                appViewStore.accountPaginationFilters.invited.defaultSort
                  ? (appViewStore.accountPaginationFilters.invited.defaultSort as keyof ReviewCrbRow)
                  : 'created_on'
              }
              defaultSortDirection={
                appViewStore.accountPaginationFilters.invited.defaultSortDirection
                  ? appViewStore.accountPaginationFilters.invited.defaultSortDirection
                  : 'desc'
              }
              onSort={(sort, sortDirection) => {
                appViewStore.accountPaginationFilters.invited.defaultSort = sort;
                appViewStore.accountPaginationFilters.invited.defaultSortDirection = sortDirection;
              }}
              defaultRowCount={
                appViewStore.accountPaginationFilters.invited.pageSize
                  ? appViewStore.accountPaginationFilters.invited.pageSize
                  : 10
              }
              onRowsPerPageChange={(pageSize: number) => {
                appViewStore.accountPaginationFilters.invited.pageSize = pageSize;
              }}
              showFilterZeroState={showFilterZeroState}
              clickable
              onClick={handleOnView}
              page={appViewStore.accountPaginationFilters.invited.page}
              onPageChange={(page) => {
                appViewStore.accountPaginationFilters.invited.page = page;
              }}
              selectable={model.canUserChangeStatus}
              returnSelection={(selected) => {
                setSelectedAccounts(selected);
              }}
              actions={
                <Container padding="0" align="center">
                  <ContainerItem>
                    <Text type="table-header" content={`${selectedAccounts.length} Selected`} />
                  </ContainerItem>

                  <Button
                    label={'Change Status'}
                    onClick={() => setChangeStatusModalOpen(true)}
                    color={'text'}
                    startIcon={<SwapHorizIcon />}
                  />
                </Container>
              }
            />
          </TableContainer>
        ) : (
          <ZeroState
            title={'You have no invited accounts'}
            subTitle={
              "Let's fix that! Invite an account by using the button above. The account will then receive an email inviting them to complete due diligence on Green Check's platform."
            }
            icon={'/img/empty-states/invited-account.svg'}
          />
        )}

        <UnarchiveInvitedAccountModal
          isOpen={unarchiveModalOpen}
          onClose={() => setUnarchiveModalOpen(false)}
          orgName={orgNameQuery}
        />
      </Box>
      <ChangeStatusModal
        isOpen={changeStatusModalOpen}
        onClose={() => setChangeStatusModalOpen(false)}
        selectedAccounts={selectedAccounts}
        setSelectedAccounts={setSelectedAccounts}
        initialStatus={DueDiligenceStatus.BANK_PENDING}
      />
    </>
  );
});
